import { useState, useEffect, useCallback, useRef } from 'react';
import { Asset } from '../../state/types';
import { AssetFilters, ImageFilter, defaultFilters } from '../../components/assets/config';
import { useSession } from '../../context/SessionContext';
import { useAssetApi } from '../../api/useAssetApi';

const ASSET_NAV_STATE_KEY = 'assetsNavState';
const LEGACY_ASSET_NAV_STATE_KEY = 'vehiclesNavState';
const ASSET_RPP_KEY = 'assetsRowsPerPage';
const LEGACY_ASSET_RPP_KEY = 'vehiclesRowsPerPage';

const loadInitialState = () => {
  const stored = localStorage.getItem(ASSET_NAV_STATE_KEY) || localStorage.getItem(LEGACY_ASSET_NAV_STATE_KEY);
  if (!stored) return { page: 1, filters: {}, resultsPerPage: 10 };
  try {
    const parsed = JSON.parse(stored);
    return {
      page: parsed.page || 1,
      filters: parsed.filters || defaultFilters,
      resultsPerPage: parsed.resultsPerPage || 10,
    };
  } catch {
    return { page: 1, filters: defaultFilters, resultsPerPage: 10 };
  }
};

const loadRowsPerPage = () => {
  const stored = localStorage.getItem(ASSET_RPP_KEY) || localStorage.getItem(LEGACY_ASSET_RPP_KEY);
  const n = stored ? Number(stored) : NaN;
  return [10, 25, 50, 100].includes(n) ? n : 10;
};

export const useAssets = () => {
  const { page: initialPage, filters: initialFilters } = loadInitialState();
  const initialRPP = loadRowsPerPage();
  const { selectedSystem, isLoading: sessionIsLoading, setIsLoading } = useSession();
  const { getAssetsApi } = useAssetApi();

  const [assets, setAssets] = useState<Asset[]>([]);
  const [totalAssets, setTotalAssets] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [filters, setFilters] = useState<AssetFilters>(initialFilters);
  const [resultsPerPage, setResultsPerPage] = useState(initialRPP);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    setFilters(defaultFilters);
    setPage(1);
  }, [selectedSystem]);

  useEffect(() => {
    localStorage.setItem(ASSET_RPP_KEY, String(resultsPerPage));
    setPage(1);
  }, [resultsPerPage]);

  const fetchAssets = useCallback(async () => {
    if (sessionIsLoading || !selectedSystem) {
      return;
    }

    setIsLoading(true);
    if (!selectedSystem) {
      setAssets([]);
      setTotalAssets(0);
      setIsLoading(false);
      return;
    }
    try {
      const {
        croppedImages,
        bgrImages,
        exteriorImages,
        featuredImages,
        manualImages,
        panoramicImages,
        ucImages,
        ...otherFilters
      } = filters;

      const payload: Record<string, any> = { ...otherFilters };

      const imageFilters: Record<string, ImageFilter | undefined> = {
        croppedImages,
        bgrImages,
        exteriorImages,
        featuredImages,
        manualImages,
        panoramicImages,
        ucImages,
      };

      for (const key in imageFilters) {
        const filter = imageFilters[key];
        if (filter?.checked) {
          payload[key] = {
            checked: true,
            operator: filter.operator || '=', // Default to '=' if not set
            imageCount: filter.imageCount ?? 0,   // Default to 0 if not set
          };
        }
      }

      const body = {
        ...payload,
        page,
        per_page: resultsPerPage,
        system_code: selectedSystem.systemCode,
        sort_direction: sortDirection,
      };
      const { assets: translatedAssets, total } = await getAssetsApi(body);
      setAssets(translatedAssets);
      setTotalAssets(total);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSystem, filters, page, resultsPerPage, sortDirection]);

  useEffect(() => {
    const savedState = { page, filters, resultsPerPage };
    localStorage.setItem(ASSET_NAV_STATE_KEY, JSON.stringify(savedState));
  }, [page, filters, resultsPerPage]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchAssets();
    }, 150);
    return () => clearTimeout(debounceTimer);
  }, [fetchAssets]);

  const updateFilters = (newFilters: AssetFilters) => {
    setPage(1);
    setFilters(newFilters);
  };

  return {
    assets,
    totalAssets,
    page,
    setPage,
    resultsPerPage,
    setResultsPerPage,
    sortDirection,
    setSortDirection,
    filters,
    updateFilters,
    refetch: fetchAssets,
    selectedAssets,
    setSelectedAssets
  };
};
